import { useState } from 'react';
import { Link } from 'react-router';
import { Reveal } from '@components/motion';
import FigmaFrame from './FigmaFrame';

const COVER_MAIN = '/landing/report/cover-main.webp';
const THUMB_1 = '/landing/report/thumb-1.webp';
const THUMB_2 = '/landing/report/thumb-2.webp';
const THUMB_3 = '/landing/report/thumb-3.webp';
const AVATAR_1 = '/landing/report/avatar-1.webp';
const AVATAR_2 = '/landing/report/avatar-2.webp';
const AVATAR_3 = '/landing/report/avatar-3.webp';

interface TeamMember {
    name: string;
    /** A portrait. Members from the CMS arrive with a name only. */
    avatar?: string;
}

interface Publication {
    titleDark: string;
    titleGrey: string;
    paragraph: string;
    cover: string;
    thumb: string;
    published: string;
    pages: string;
    format: string;
    language: string;
    team: TeamMember[];
}
export type { Publication };

const TEAM: TeamMember[] = [
    { name: 'Nelson Olanipekun', avatar: AVATAR_1 },
    { name: 'Ada Okafor', avatar: AVATAR_2 },
    { name: 'Rita Odafe', avatar: AVATAR_3 },
    { name: 'Sylvester Agih', avatar: AVATAR_1 },
    { name: 'Musa Bello', avatar: AVATAR_2 },
    { name: 'Chidi Nwosu', avatar: AVATAR_1 },
    { name: 'Amina Yusuf', avatar: AVATAR_3 },
];

/** Every publication carries the full left-side content it swaps in when selected. */
export const PUBLICATIONS: Publication[] = [
    {
        titleDark: 'The Stories Behind ',
        titleGrey: 'the Numbers',
        paragraph:
            'An evaluation of how quickly state high courts delivered justice before and during the pandemic, drawn from registry returns, sampled case files and interviews with practitioners in each state.',
        cover: COVER_MAIN,
        thumb: THUMB_1,
        published: 'May, 2024',
        pages: '24',
        format: 'PDF',
        language: 'English',
        team: TEAM,
    },
    {
        titleDark: 'Executive Patronage ',
        titleGrey: 'and Judicial Autonomy',
        paragraph:
            'A decade of gifts, allowances and benefits granted by state executives to the bench — what was given, by whom, and what it means for the independence of the courts receiving it.',
        cover: THUMB_2,
        thumb: THUMB_2,
        published: 'Jan, 2025',
        pages: '48',
        format: 'PDF',
        language: 'English',
        team: [...TEAM.slice(2), ...TEAM.slice(0, 2)],
    },
    {
        titleDark: 'Judicial Code of ',
        titleGrey: 'Conduct and Infractions',
        paragraph:
            'A review of how judicial misconduct is defined, reported and sanctioned in practice, and of the gap between the conduct rules on paper and the outcomes that actually follow a complaint.',
        cover: THUMB_3,
        thumb: THUMB_3,
        published: 'Mar, 2025',
        pages: '36',
        format: 'PDF',
        language: 'English',
        team: [...TEAM.slice(4), ...TEAM.slice(0, 4)],
    },
    {
        titleDark: 'Half a Year, ',
        titleGrey: 'Full Impact',
        paragraph:
            'Six months of casework in numbers: matters received, matters resolved, people released, and an honest account of the ones that stalled and what we think stopped them from moving.',
        cover: THUMB_1,
        thumb: THUMB_1,
        published: 'Jul, 2025',
        pages: '52',
        format: 'PDF',
        language: 'English',
        team: [...TEAM.slice(3), ...TEAM.slice(0, 3)],
    },
];

/** Chevron glyph (Figma "keyboard_arrow_up"), points up by default. */
const Chevron = ({ className }: { className?: string }) => (
    <svg viewBox="0 0 19.6716 11.8132" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
        <path
            d="M10.5429 5.07623C10.1524 4.6857 9.5192 4.6857 9.12868 5.07623L2.68456 11.5203C2.29403 11.9109 1.66087 11.9109 1.27035 11.5203L0.292894 10.5429C-0.0976308 10.1524 -0.0976314 9.5192 0.292893 9.12868L9.12868 0.292893C9.5192 -0.0976312 10.1524 -0.0976309 10.5429 0.292893L19.3787 9.12868C19.7692 9.5192 19.7692 10.1524 19.3787 10.5429L18.4012 11.5203C18.0107 11.9109 17.3775 11.9109 16.987 11.5203L10.5429 5.07623Z"
            fill="#1D1B20"
        />
    </svg>
);

/** Arrow-up-right glyph used inside the "View All" black circle. */
const ArrowUpRight = ({ className }: { className?: string }) => (
    <svg viewBox="0 0 24 24" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
        <path
            d="M7 17L17 7M17 7H8M17 7V16"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

interface ReportProps {
    /** Greyed lead-in of the section heading. */
    headingLead?: string;
    /** Bold remainder of the section heading. */
    headingBold?: string;
    /** The "View All" circle links out to the publications page — hide it on that page. */
    showViewAll?: boolean;
}

function ReportDesktop({ headingLead = 'Our', headingBold = 'Publications', showViewAll = true }: ReportProps) {
    const [active, setActive] = useState(0);
    const pub = PUBLICATIONS[active];

    return (
        <FigmaFrame designWidth={1920} designHeight={1069} className="w-full">
            <div className="relative h-[1069px] w-[1920px] bg-white text-[#1b1b1b]">
                {/* Heading (static) */}
                <Reveal
                    as="p"
                    once
                    variant="fade-up"
                    className="absolute left-[141px] top-[97.5px] w-[1346px] -translate-y-1/2 text-[96px] font-extrabold leading-[103px]"
                >
                    <span className="font-normal text-[#6c6c6c]">{headingLead}</span>
                    <span className="font-normal"> </span>
                    <span>{headingBold}</span>
                </Reveal>

                {/* View All (static) */}
                {showViewAll && (
                    <Reveal as="div" once variant="scale" delay={200} className="absolute left-[1213px] top-[98px]">
                        <Link to="/resources/publications" className="flex items-center gap-[17px]">
                            <span className="relative size-[124px]">
                                <span className="absolute inset-0 rounded-full bg-[#1b1b1b]" />
                                <ArrowUpRight className="absolute left-[24px] top-[24px] size-[75px]" />
                            </span>
                            <span className="whitespace-nowrap text-[24px] font-bold leading-[32px] text-[#1b1b1b]">
                                View All
                            </span>
                        </Link>
                    </Reveal>
                )}

                {/* Cover image group — swaps (with a slide-in) per selected publication */}
                <Reveal as="div" once variant="fade-up" duration={1000} className="absolute left-[147px] top-[364px]">
                    <div key={active} className="cg-sol-enter">
                        <div className="absolute left-0 top-[35px] size-[431px] rounded-full bg-[#d9d9d9]" />
                        {/* Covers arrive both ways round — page-one scans are portrait, older
                            feature images are landscape — so the whole cover is fitted inside
                            the frame rather than cropped to fill it. The white backing reads
                            as the page it is when the two shapes disagree. */}
                        <div className="absolute left-[59px] top-0 h-[447px] w-[314px] bg-white shadow-[48px_52px_70.6px_8px_rgba(0,0,0,0.31)]">
                            <img
                                src={pub.cover}
                                alt=""
                                className="pointer-events-none absolute inset-0 size-full object-contain"
                            />
                        </div>
                    </div>
                </Reveal>

                {/* Download button (per publication, position static) */}
                <Link
                    to="/resources/publications"
                    className="absolute left-[255px] top-[888.5px] flex w-[216px] -translate-y-1/2 items-center justify-center rounded-[10px] bg-[#1b1b1b] px-[20px] py-[10px] text-[16px] font-bold leading-[24px] text-white"
                >
                    Download
                </Link>

                {/* Right column content (title / paragraph / metadata) — swaps per selection.
                    Flow layout, not fixed offsets: a live title can run to three lines where
                    the bundled two-line samples never did, and fixed offsets let a long one
                    overlap the paragraph below it. */}
                <Reveal
                    as="div"
                    once
                    variant="fade-up"
                    delay={160}
                    className="absolute left-[714px] top-[364px] w-[559px]"
                >
                    <div key={active} className="cg-sol-enter flex flex-col gap-[24px]">
                        {/* Title */}
                        <p className="w-[559px] text-[40px] font-extrabold leading-[50px]">
                            <span>{pub.titleDark}</span>
                            <span className="text-[#a5a5a5]">{pub.titleGrey}</span>
                        </p>

                        {/* Paragraph */}
                        <p className="w-[448px] text-[14px] font-normal leading-[24px] text-[#333]">{pub.paragraph}</p>

                        {/* Divider */}
                        <div className="h-px w-[448px] bg-[#e0e0e0]" />

                        {/* Metadata grid — a row only when the CMS has that detail; imported publications start without them */}
                        <div className="relative h-[63px] w-[395px] text-[16px] leading-[24px]">
                            {pub.published && (
                                <div className="absolute left-0 top-0 flex items-center gap-[15px]">
                                    <span className="font-normal text-[#6c6c6c]">Published:</span>
                                    <span className="font-bold text-[#1b1b1b]">{pub.published}</span>
                                </div>
                            )}
                            {pub.pages && (
                                <div className="absolute left-[243px] top-0 flex items-center gap-[15px]">
                                    <span className="font-normal text-[#6c6c6c]">No. Of Pages:</span>
                                    <span className="font-bold text-[#1b1b1b]">{pub.pages}</span>
                                </div>
                            )}
                            {pub.format && (
                                <div className="absolute left-0 top-[39px] flex items-center gap-[15px]">
                                    <span className="font-normal text-[#6c6c6c]">Format:</span>
                                    <span className="font-bold text-[#1b1b1b]">{pub.format}</span>
                                </div>
                            )}
                            {pub.language && (
                                <div className="absolute left-[243px] top-[39px] flex items-center gap-[15px]">
                                    <span className="font-normal text-[#6c6c6c]">Language:</span>
                                    <span className="font-bold text-[#1b1b1b]">{pub.language}</span>
                                </div>
                            )}
                        </div>
                    </div>
                </Reveal>

                {/* The Team — swaps per selection */}
                {/* <Reveal
                    as="div"
                    once
                    variant="fade-up"
                    delay={280}
                    className="absolute left-[714px] top-[812px] flex w-[720px] flex-col items-start gap-[17px]"
                >
                    <div key={active} className="cg-sol-enter flex w-full flex-col gap-[17px]">
                        <p className="w-full text-[18px] font-bold leading-[28px] text-[#1b1b1b]">The Team</p>
                        <div className="flex w-full items-center gap-[30px]">
                            <button type="button" className="flex size-[41px] shrink-0 items-center justify-center">
                                <Chevron className="h-[11.81px] w-[19.67px] -rotate-90" />
                            </button>
                            <div className="scrollbar-hide flex w-[578px] items-center gap-[26px] overflow-x-auto overflow-y-clip">
                                {pub.team.map((m, i) => (
                                    <div key={i} className="flex shrink-0 flex-col items-center gap-[10px]">
                                        <img
                                            src={m.avatar}
                                            alt=""
                                            className="size-[55px] shrink-0 rounded-full object-cover"
                                        />
                                        <p className="whitespace-nowrap text-center text-[14px] font-normal leading-[24px] text-[#1b1b1b]">
                                            {m.name}
                                        </p>
                                    </div>
                                ))}
                            </div>
                            <button type="button" className="flex size-[41px] shrink-0 items-center justify-center">
                                <Chevron className="h-[11.81px] w-[19.67px] rotate-90" />
                            </button>
                        </div>
                    </div>
                </Reveal> */}

                {/* Scroll-down chevron bottom-right */}
                <div className="absolute left-[1526px] top-[1060px] flex size-[41px] items-center justify-center">
                    <Chevron className="h-[11.81px] w-[19.67px] rotate-180" />
                </div>

                {/* Right rail: grey track + vertically scrollable publication cards.
                    Clicking a card swaps the whole left side; the active card gets
                    the highlighted panel + orange bar treatment. */}
                <div className="absolute left-[1542px] top-0 h-[1069px] w-[9px] bg-[#f4f4f4]" />
                <div className="scrollbar-hide absolute left-[1542px] top-0 flex h-[1069px] w-[378px] flex-col overflow-y-auto">
                    {PUBLICATIONS.map((p, i) => {
                        const isActive = i === active;
                        return (
                            <button
                                key={i}
                                type="button"
                                onClick={() => setActive(i)}
                                aria-pressed={isActive}
                                aria-label={`${p.titleDark}${p.titleGrey}`}
                                className="relative h-[326px] w-full shrink-0"
                            >
                                {/* Orange rail segment (active) */}
                                <span
                                    className={`absolute left-0 top-0 h-full w-[9px] transition-colors duration-300 ${
                                        isActive ? 'bg-[#f60]' : 'bg-transparent'
                                    }`}
                                />
                                {/* Panel background (active) */}
                                <span
                                    className={`absolute left-[9px] top-0 h-full w-[369px] transition-colors duration-300 ${
                                        isActive ? 'bg-[#f4f4f4]' : 'bg-white'
                                    }`}
                                />
                                {/* Cover thumbnail */}
                                <span
                                    className={`absolute left-[100px] top-[52px] block h-[222px] w-[190px] bg-white transition-transform duration-300 ${
                                        isActive ? 'border border-[rgba(0,0,0,0.11)]' : 'hover:scale-[1.03]'
                                    }`}
                                >
                                    <img
                                        src={p.thumb}
                                        alt=""
                                        className="pointer-events-none absolute inset-0 size-full object-contain"
                                    />
                                </span>
                            </button>
                        );
                    })}
                </div>
            </div>
        </FigmaFrame>
    );
}

function ReportMobile({ headingLead = 'Our', headingBold = 'Publications', showViewAll = true }: ReportProps) {
    // The desktop band swaps publication by clicking a card; on mobile the arrows
    // are the only way through, so they carry the same state. They wrap rather
    // than disable, so neither arrow is ever a dead control.
    const [active, setActive] = useState(0);
    const count = PUBLICATIONS.length;
    const at = (i: number) => PUBLICATIONS[((i % count) + count) % count];
    const pub = at(active);
    const step = (by: number) => setActive((i) => (((i + by) % count) + count) % count);

    return (
        <section className="bg-white pt-[45px] text-[#1b1b1b]">
            {/* Heading */}
            <h2 className="px-5 text-[40px] font-extrabold leading-[50px]">
                <span className="font-normal text-[#6c6c6c]">{headingLead} </span>
                {headingBold}
            </h2>

            {/* Cover band: side thumbnails behind a translucent overlay + center cover */}
            <div className="relative mt-[35px] h-[262px]">
                {/* Side thumbnail band */}
                <div className="absolute inset-x-0 top-[53px] h-[157px]">
                    <div className="absolute left-[20px] top-0 h-[156px] w-[135px] bg-white">
                        <img src={at(active - 1).cover} alt="" className="absolute inset-0 size-full object-contain" />
                    </div>
                    <div className="absolute left-[222px] top-0 h-[157px] w-[135px] border border-[rgba(0,0,0,0.11)] bg-white">
                        <img src={at(active + 1).cover} alt="" className="absolute inset-0 size-full object-contain" />
                    </div>
                    <div className="absolute inset-0 bg-[rgba(229,229,229,0.4)]" />
                </div>

                {/* Center cover + circular backdrop */}
                <div className="absolute left-1/2 top-0 h-[262px] -translate-x-1/2">
                    <span className="absolute left-1/2 top-[3.5px] size-[252.62px] -translate-x-1/2 rounded-full bg-[#d9d9d9]" />
                    <div
                        key={active}
                        className="cg-sol-enter relative h-[262px] w-[184px] bg-white shadow-[23px_24px_67px_8px_rgba(0,0,0,0.45)]"
                    >
                        <img src={pub.cover} alt="" className="absolute inset-0 size-full object-contain" />
                    </div>
                </div>
            </div>

            {/* Content: title, paragraph, buttons, carousel arrows */}
            <div className="mx-auto mt-[34px] flex w-[332px] max-w-full flex-col items-center gap-[38px]">
                <div key={active} className="cg-sol-enter w-full">
                    <h3 className="text-center text-[24px] font-extrabold leading-[32px]">
                        {pub.titleDark}
                        <span className="text-[#a5a5a5]">{pub.titleGrey}</span>
                    </h3>
                    <p className="mt-[18px] text-justify text-[14px] font-normal leading-[24px] text-[#333]">
                        {pub.paragraph}
                    </p>
                </div>

                <div className="flex w-full items-start gap-[15px]">
                    <Link
                        to="/resources/publications"
                        className="flex w-[160px] items-center justify-center rounded-[10px] bg-[#1b1b1b] px-5 py-[10px] text-[16px] font-bold leading-[24px] text-white"
                    >
                        Download
                    </Link>
                    {showViewAll && (
                        <Link
                            to="/resources/publications"
                            className="flex w-[160px] items-center justify-center rounded-[10px] bg-[#1b1b1b] px-5 py-[10px] text-[16px] font-bold leading-[24px] text-white"
                        >
                            View All
                        </Link>
                    )}
                </div>

                <div className="flex items-center gap-[18px]">
                    <button
                        type="button"
                        onClick={() => step(-1)}
                        aria-label="Previous publication"
                        className="flex size-[34px] items-center justify-center rounded-full bg-[#e6e6e6] transition-colors active:bg-[#d2d2d2]"
                    >
                        <Chevron className="h-[8px] w-[13px] -rotate-90" />
                    </button>
                    <span aria-live="polite" className="text-[13px] font-semibold text-[#6c6c6c]">
                        {active + 1} / {count}
                    </span>
                    <button
                        type="button"
                        onClick={() => step(1)}
                        aria-label="Next publication"
                        className="flex size-[34px] items-center justify-center rounded-full bg-[#e6e6e6] transition-colors active:bg-[#d2d2d2]"
                    >
                        <Chevron className="h-[8px] w-[13px] rotate-90" />
                    </button>
                </div>
            </div>
        </section>
    );
}

export default function Report(props: ReportProps) {
    return (
        <>
            {/* Breathing room below the section before the next one begins */}
            <div className="hidden bg-white pb-[120px] lg:block">
                <ReportDesktop {...props} />
            </div>
            <div className="bg-white pb-[64px] lg:hidden">
                <ReportMobile {...props} />
            </div>
        </>
    );
}
